/**
 * Heal substitution table + owner-relative path setter (PR1b).
 *
 * Package-private to the `referenceHeal*` module family. Keeps the
 * code → reserved-default mapping and the small JSON-path navigator out of the
 * orchestrator so `referenceHeal.ts` stays focused on the audit→clone→re-audit
 * flow (CLAUDE.md module-size discipline).
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §12 (PR1b) / §5.3.
 */

import {
  ReferenceErrorCode,
  type ReferenceFix,
  type ReferenceIssue,
} from "./referenceAudit.types";

/* ────────────────────────────────────────────────────────────────────────
 * Substitution table — code → always-present reserved default.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * For each HEALABLE error code, the always-present built-in default value the
 * dangling reference is re-pointed at (AutoCAD AUDIT/RECOVER + ezdxf Auditor):
 *
 *   - UNDEFINED_LAYER            → "0"          (default layer, never purgeable)
 *   - UNDEFINED_TEXT_STYLE       → "Standard"   (default STYLE)
 *   - UNDEFINED_DIMENSION_STYLE  → "Standard"   (default DIMSTYLE)
 *   - UNDEFINED_MULTILEADER_STYLE→ "Standard"   (default MLEADERSTYLE — the seed
 *                                                default name in model.ts)
 *   - UNDEFINED_LINETYPE         → "Continuous" (reserved LTYPE)
 *   - UNDEFINED_ANNOTATION_SCALE → "1:1"        (built-in CANNOSCALE)
 *
 * `UNDEFINED_BLOCK` is deliberately ABSENT — a missing block definition has no
 * safe substitute (see `referenceHeal.ts`); it stays an error in the report.
 */
export const HEAL_DEFAULTS = {
  [ReferenceErrorCode.UNDEFINED_LAYER]: "0",
  [ReferenceErrorCode.UNDEFINED_TEXT_STYLE]: "Standard",
  [ReferenceErrorCode.UNDEFINED_DIMENSION_STYLE]: "Standard",
  [ReferenceErrorCode.UNDEFINED_MULTILEADER_STYLE]: "Standard",
  [ReferenceErrorCode.UNDEFINED_LINETYPE]: "Continuous",
  [ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE]: "1:1",
} as const satisfies Partial<Record<ReferenceErrorCode, string>>;

/** True iff an error code has a reserved-default substitution. */
export function isHealableCode(code: ReferenceErrorCode): boolean {
  return code in HEAL_DEFAULTS;
}

/**
 * True iff an audit issue can be value-substituted by heal — the SINGLE source
 * of truth shared by `healDrawingReferences` (whole-doc) and `healEntityRefs`
 * (per-entity streaming). An issue is healable iff its code carries a reserved
 * default AND it is not the `annotativeDecompositions` Record KEY (which is a
 * key, not a navigable value path — out of heal scope, report-only).
 */
export function isHealableIssue(issue: ReferenceIssue): boolean {
  if (!isHealableCode(issue.code)) return false;
  // annotativeDecompositions KEY is a Record key, not a navigable value path —
  // out of heal scope (report-only). The audit flags it with this exact field.
  if (issue.field === "annotativeDecompositions.key") return false;
  return true;
}

/**
 * Apply ONE healable issue's reserved-default substitution at its owner-relative
 * `path` on `root` (the mutable owner record that holds the offending value).
 * The SINGLE rule-application primitive shared by the whole-document and the
 * per-entity heal paths — both feed an owner root + an owner-relative issue and
 * get back a `ReferenceFix` (or `null` when the path is not navigable / the code
 * is not in the heal table, in which case the ref is left as a reported error).
 *
 * PURE w.r.t. control flow (never throws); MUTATES `root` in place at the final
 * path step only (heal never creates structure — it re-points an existing
 * dangling value). Callers that need input purity clone the owner BEFORE calling.
 */
export function applyHealIssue(
  root: Record<string, unknown>,
  issue: ReferenceIssue,
): ReferenceFix | null {
  const to = HEAL_DEFAULTS[issue.code as keyof typeof HEAL_DEFAULTS];
  if (to === undefined) return null; // not in the heal table (defensive)
  const applied = setByPath(root, issue.path, to);
  if (!applied) return null; // path not navigable — leave as error (report-only)
  // Codex#PR1b P2 — an annotation-scale NAME and its paired NUMERIC FACTOR are
  // a unit: `$CANNOSCALE`/`$CANNOSCALEVALUE` (document) and a Viewport's
  // `viewportCannoscaleName`/`viewportCannoscaleFactor`. Healing only the NAME
  // to "1:1" while leaving the old factor (e.g. 0.02 for a missing "1:50")
  // would render/export at the wrong scale. Reset the paired factor on the SAME
  // owner root to the HEALED scale's factor (1 for "1:1"). Only the name fields
  // emit `UNDEFINED_ANNOTATION_SCALE` at a known sibling-factor field; the
  // `annotationScales[i]` member case has no paired sibling and is skipped.
  resetPairedAnnotationFactor(root, issue.code, issue.field);
  return {
    code: issue.code,
    ownerKind: issue.ownerKind,
    ownerId: issue.ownerId,
    container: issue.container,
    path: issue.path,
    refKind: issue.refKind,
    field: issue.field,
    from: issue.referencedKey,
    to,
    message: `healed ${issue.field} "${issue.referencedKey}" → "${to}"`,
  };
}

/**
 * The numeric scale factor of the healed annotation-scale default ("1:1").
 * AcDbScale stores `paperUnits/drawingUnits`; for 1:1 that is exactly 1.
 */
const HEALED_ANNOTATION_SCALE_FACTOR = 1;

/**
 * Reset the NUMERIC factor paired with a healed annotation-scale NAME on the
 * SAME owner `root`. Maps the emitting name field → its sibling factor field:
 *   - "cannoscaleName"          (document) → "cannoscale"
 *   - "viewportCannoscaleName"  (Viewport) → "viewportCannoscaleFactor"
 *
 * Only runs for `UNDEFINED_ANNOTATION_SCALE` NAME heals; the
 * `annotationScales[i]` array-member case (no paired factor) is left untouched.
 * Re-points the factor in place only when the slot already exists (heal never
 * creates structure — an absent factor means the model default already applies).
 * PURE / never throws.
 */
function resetPairedAnnotationFactor(
  root: Record<string, unknown>,
  code: ReferenceErrorCode,
  field: string,
): void {
  if (code !== ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE) return;
  let factorField: string | undefined;
  if (field === "cannoscaleName") factorField = "cannoscale";
  else if (field === "viewportCannoscaleName") {
    factorField = "viewportCannoscaleFactor";
  }
  if (factorField === undefined) return;
  // Re-point an existing factor slot only — an absent slot means the schema
  // default (1) already governs, so there is nothing dirty to reset.
  if (factorField in root) {
    root[factorField] = HEALED_ANNOTATION_SCALE_FACTOR;
  }
}

/* ────────────────────────────────────────────────────────────────────────
 * Built-in default-record guarantee (whole-doc heal — Codex#PR1b P2).
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * The minimal shape of the style tables this fixup touches. Kept structural
 * (not the full `StyleTables` import) so `referenceHeal.rules` stays decoupled
 * from `model.ts` — the orchestrator owns the typed doc and passes the tables.
 */
interface NamedStyleTables {
  textStyles: { name: string }[];
  dimensionStyles: { name: string }[];
  multileaderStyles: { name: string }[];
}

/** Builders for the always-present "Standard" records (single-source seeds). */
interface DefaultRecordBuilders {
  textStyle: () => { name: string };
  dimensionStyle: () => { name: string };
  multileaderStyle: () => { name: string };
}

/**
 * Guarantee the always-present built-in target record exists in `tables` when a
 * STYLE / DIMSTYLE / MLEADERSTYLE ref was healed to its default ("Standard").
 *
 * A legal-but-dirty DXF can carry a NON-EMPTY style table that OMITS "Standard"
 * (AutoCAD always has it; `importStyleTables` only auto-seeds it for the EMPTY
 * table). Healing a dangling ref → "Standard" would then re-point at a still-
 * missing record, leaving the post-heal audit dirty and export carrying a
 * dangling ref. We ADD the built-in default record (the SAME minimal seed the
 * empty-table import branch uses, supplied via `builders`) when absent.
 *
 * WHOLE-DOC ONLY: the per-entity streaming heal (`healEntityRefs`) heals entity
 * refs, NOT tables — the streaming shell's tables are seeded once via
 * `importStyleTables` (which the wiring guarantees seeds "Standard"). This is a
 * no-op for layer / linetype / annotation-scale codes (those defaults — "0" /
 * "Continuous" / "1:1" — are seeded by the importers/schema and the audit treats
 * them as always-present, so no table fixup is needed). PURE / never throws —
 * mutates only the passed (already-cloned) `tables`.
 */
export function ensureHealDefaultTableRecord(
  tables: NamedStyleTables,
  code: ReferenceErrorCode,
  builders: DefaultRecordBuilders,
): void {
  const seedIfMissing = (
    table: { name: string }[],
    name: string,
    build: () => { name: string },
  ): void => {
    if (!Array.isArray(table)) return; // defensive — never throw on a raw doc
    // PR1b Codex P2 (iter-3) — case-insensitive match, mirroring the audit /
    // resolution `nameIndex` (which lowercases every key). A table already
    // carrying "STANDARD" / "standard" must NOT get a SECOND "Standard" record
    // — that yields DUPLICATE_TABLE_ENTRY_NAME + two case-equivalent names on
    // export. Same guard as `importStyleTables`' Standard seed.
    const wanted = name.toLowerCase();
    if (table.some((r) => r?.name?.toLowerCase() === wanted)) return;
    table.unshift(build());
  };

  switch (code) {
    case ReferenceErrorCode.UNDEFINED_TEXT_STYLE:
      seedIfMissing(tables.textStyles, "Standard", builders.textStyle);
      break;
    case ReferenceErrorCode.UNDEFINED_DIMENSION_STYLE:
      seedIfMissing(
        tables.dimensionStyles,
        "Standard",
        builders.dimensionStyle,
      );
      break;
    case ReferenceErrorCode.UNDEFINED_MULTILEADER_STYLE:
      seedIfMissing(
        tables.multileaderStyles,
        "Standard",
        builders.multileaderStyle,
      );
      break;
    default:
      // layer / linetype / annotation-scale defaults are always-present —
      // no table record to seed.
      break;
  }
}

/* ────────────────────────────────────────────────────────────────────────
 * Owner-relative JSON path navigator.
 * ──────────────────────────────────────────────────────────────────────── */

type PathToken =
  | { kind: "key"; key: string }
  | { kind: "index"; index: number };

/**
 * Tokenise an audit `path` into property / index steps. Handles the three
 * forms the audit emits (referenceAudit.entityRefs / .structural):
 *   - dotted property            "styleOverrides.dimblk"
 *   - array index                "frozenLayers[0]" / "annotationScales[2]"
 *   - quoted Record key          "annotativeDecompositions['1:50']" /
 *                                "linetypes[\"b\"]"  (heal does NOT target these
 *                                 today, but the tokenizer accepts them so a
 *                                 future code can reuse it safely)
 *
 * Returns `undefined` for a path it cannot parse (heal then leaves the ref as a
 * reported error rather than guessing — never throws).
 */
function tokenizePath(path: string): PathToken[] | undefined {
  const tokens: PathToken[] = [];
  let i = 0;
  const n = path.length;
  // A path starts with a property name; subsequent properties follow a ".".
  // Bracket groups ([...]) may follow any property with no preceding ".".
  let expectKey = true;
  while (i < n) {
    const ch = path[i];
    if (ch === ".") {
      expectKey = true;
      i += 1;
      continue;
    }
    if (ch === "[") {
      const close = path.indexOf("]", i);
      if (close === -1) return undefined;
      const inner = path.slice(i + 1, close).trim();
      i = close + 1;
      expectKey = false;
      if (/^\d+$/.test(inner)) {
        tokens.push({ kind: "index", index: Number(inner) });
      } else {
        // Quoted Record key: 'x' or "x".
        const m = /^(['"])(.*)\1$/.exec(inner);
        if (!m) return undefined;
        tokens.push({ kind: "key", key: m[2] });
      }
      continue;
    }
    // Bare property name — read up to the next "." or "[".
    if (!expectKey) return undefined;
    let j = i;
    while (j < n && path[j] !== "." && path[j] !== "[") j += 1;
    const name = path.slice(i, j);
    if (name === "") return undefined;
    tokens.push({ kind: "key", key: name });
    i = j;
    expectKey = false;
  }
  return tokens.length > 0 ? tokens : undefined;
}

/**
 * Set the value at `path` (owner-relative) on `root` to `value`. Returns `true`
 * iff the full path resolved to an existing, writable string slot — otherwise
 * `false` (heal then leaves the ref reported, never throws). Only the FINAL
 * step is written; intermediate steps must already exist (heal never creates
 * structure — it only re-points existing dangling values).
 */
export function setByPath(
  root: Record<string, unknown>,
  path: string,
  value: string,
): boolean {
  const tokens = tokenizePath(path);
  if (!tokens) return false;

  let cur: unknown = root;
  for (let t = 0; t < tokens.length - 1; t += 1) {
    cur = step(cur, tokens[t]);
    if (cur === undefined || cur === null || typeof cur !== "object") {
      return false;
    }
  }

  const last = tokens[tokens.length - 1];
  if (last.kind === "index") {
    if (!Array.isArray(cur)) return false;
    if (last.index < 0 || last.index >= cur.length) return false;
    cur[last.index] = value;
    return true;
  }
  if (typeof cur !== "object" || cur === null || Array.isArray(cur)) {
    return false;
  }
  const obj = cur as Record<string, unknown>;
  // Re-point an existing slot only (the dangling value was there to begin
  // with). We allow an empty-string slot (a required-but-empty ref the audit
  // flagged) to be filled too.
  if (!(last.key in obj)) return false;
  obj[last.key] = value;
  return true;
}

/** Navigate one token into `cur`. */
function step(cur: unknown, token: PathToken): unknown {
  if (cur === null || typeof cur !== "object") return undefined;
  if (token.kind === "index") {
    return Array.isArray(cur) ? cur[token.index] : undefined;
  }
  return (cur as Record<string, unknown>)[token.key];
}
